/**
 * Link Synchronizer Module
 * 
 * Synchronizes image links with Kadence button links (source of truth).
 * Cleans trailing -/ patterns from URLs and ensures sibling images
 * share the same link as their adjacent button.
 */

/**
 * Remove trailing -/ patterns from URL
 */
function cleanupUrl(url) {
    return url.replace(/(-\/)+$/, '');
}

/**
 * Sync figure link with source URL
 */
function synchronizeFigureLink(figure, sourceUrl) {
    const img = figure.querySelector('img');
    
    if (!img) {
        return false;
    }
    
    const existingAnchor = figure.querySelector('a');
    
    if (existingAnchor) {
        const currentHref = existingAnchor.getAttribute('href');
        const cleanedCurrentHref = cleanupUrl(currentHref);
        
        if (cleanedCurrentHref !== sourceUrl || currentHref !== cleanedCurrentHref) {
            existingAnchor.setAttribute('href', sourceUrl);
            return true;
        }
        
        return false;
    }
    
    // Wrap image in new anchor
    const newAnchor = document.createElement('a');
    newAnchor.setAttribute('href', sourceUrl);
    newAnchor.setAttribute('draggable', 'false');
    img.parentNode.insertBefore(newAnchor, img);
    newAnchor.appendChild(img);
    
    return true;
}

/**
 * Wrap element in anchor or update existing anchor href
 */
function wrapOrUpdateElementLink(element, sourceUrl) {
    if (!element || !sourceUrl || sourceUrl === '#' || sourceUrl === '') {
        return false;
    }

    const existingAnchor = element.closest('a');

    if (existingAnchor) {
        const currentHref = existingAnchor.getAttribute('href');
        const cleanedCurrentHref = cleanupUrl(currentHref);

        if (cleanedCurrentHref !== sourceUrl || currentHref !== cleanedCurrentHref) {
            existingAnchor.setAttribute('href', sourceUrl);
            return true;
        }
        return false;
    }

    const newAnchor = document.createElement('a');
    newAnchor.setAttribute('href', sourceUrl);
    newAnchor.setAttribute('draggable', 'false');
    element.parentNode.insertBefore(newAnchor, element);
    newAnchor.appendChild(element);

    return true;
}

/**
 * Process catalog-inline-nav: wrap cover background span with button link
 */
function processCatalogInlineNav(navGroup) {
    const kadenceAnchor = navGroup.querySelector('.wp-block-kadence-advancedbtn a[href]');
    const coreAnchor = navGroup.querySelector('.wp-block-button .wp-block-button__link[href]');
    const anchor = kadenceAnchor || coreAnchor;

    if (!anchor) {
        return;
    }

    const href = anchor.getAttribute('href');

    if (!href || href === '#' || href === '') {
        return;
    }

    const sourceUrl = cleanupUrl(href);

    if (href !== sourceUrl) {
        anchor.setAttribute('href', sourceUrl);
    }

    const coverBackground = navGroup.querySelector('.wp-block-cover__background');

    if (coverBackground) {
        wrapOrUpdateElementLink(coverBackground, sourceUrl);
    }
}

/**
 * Find sibling figures sharing the same parent
 */
function findSiblingFigures(kadenceButton) {
    const parent = kadenceButton.parentElement;
    
    if (!parent) {
        return [];
    }
    
    const siblingFigures = [];
    
    for (const child of parent.children) {
        if (child.matches('figure.wp-block-image')) {
            siblingFigures.push(child);
        }
    }
    
    return siblingFigures;
}

/**
 * Process button and sync sibling figures
 */
function processKadenceButton(kadenceButton) {
    const anchor = kadenceButton.querySelector('a[href]');
    
    if (!anchor) {
        return;
    }
    
    const href = anchor.getAttribute('href');
    
    if (!href || href === '#' || href === '') {
        return;
    }
    
    const sourceUrl = cleanupUrl(href);
    
    // Clean button href if needed
    if (href !== sourceUrl) {
        anchor.setAttribute('href', sourceUrl);
    }
    
    // Sync sibling figures
    const siblingFigures = findSiblingFigures(kadenceButton);
    
    siblingFigures.forEach(function(figure) {
        synchronizeFigureLink(figure, sourceUrl);
    });
}

/**
 * Initialize Link Synchronizer
 */
export default function initLinkSynchronizer() {
    const kadenceButtons = document.querySelectorAll('.wp-block-kadence-advancedbtn');

    kadenceButtons.forEach(function(button) {
        processKadenceButton(button);
    });

    const catalogNavs = document.querySelectorAll('.is-style-catalog-inline-nav');

    catalogNavs.forEach(function(nav) {
        processCatalogInlineNav(nav);
    });
}
